import { useState, useEffect } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { useConfig } from "../../hooks/useConfig";
import { AccessBlocked } from "../../components/AccessBlocked";

export default function CRMAdminPage() {
  const { getValues } = useConfig();
  const verticals = getValues("crm_verticals");
  const [tab, setTab] = useState("pipelines");
  const [pipelines, setPipelines] = useState([]);
  const [cadences, setCadences] = useState([]);
  const [assignRules, setAssignRules] = useState([]);
  const [healthCfg, setHealthCfg] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [blocked, setBlocked] = useState("");

  // Pipeline builder state
  const [editPipeline, setEditPipeline] = useState(null);
  const [pipelineForm, setPipelineForm] = useState({ name: "", vertical: "", stages: [] });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/crm/pipelines"), api.get("/crm/cadences"),
      api.get("/crm/assignment-rules"), api.get("/crm/health-config"),
    ]).then(([p, c, a, h]) => {
      setPipelines(p.data || []); setCadences(c.data || []);
      setAssignRules(a.data || []); setHealthCfg(h.data || {});
    }).catch(e => {
      // FX3b item 8 — plain English on a 403, never the permission key
      if (e.response?.status === 403) { setBlocked(e.response?.data?.detail || "forbidden"); return; }
      setErr(e.response?.data?.detail || "Failed to load the CRM settings");
    })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const savePipeline = async () => {
    try {
      if (editPipeline) {
        await api.put(`/crm/pipelines/${editPipeline}`, pipelineForm);
      } else {
        await api.post("/crm/pipelines", pipelineForm);
      }
      toast.success("Pipeline saved");
      setEditPipeline(null);
      setPipelineForm({ name: "", vertical: "", stages: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addStage = () => {
    setPipelineForm(f => ({ ...f, stages: [...f.stages, { key: "", name: "", entry_required_fields: [], sla_hours: 48, default_next_action: { action_type: "follow_up_call", channel: "phone", due_offset_hours: 24 }, cadence_id: null, is_terminal: false, terminal_outcome: null }] }));
  };

  const updateStage = (idx, field, val) => {
    setPipelineForm(f => {
      const stages = [...f.stages];
      if (field.startsWith("dna.")) {
        const dnaField = field.split(".")[1];
        stages[idx] = { ...stages[idx], default_next_action: { ...stages[idx].default_next_action, [dnaField]: val } };
      } else {
        stages[idx] = { ...stages[idx], [field]: val };
      }
      return { ...f, stages };
    });
  };

  const removeStage = (idx) => setPipelineForm(f => ({ ...f, stages: f.stages.filter((_, i) => i !== idx) }));

  if (blocked) return <div className="p-8"><AccessBlocked detail={blocked} testid="crm-access-blocked" /></div>;
  if (err) return <div className="p-8"><p className="text-zinc-500 text-sm">{err}</p></div>;

  return (
    <div data-testid="crm-admin-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mb-1">CRM Admin</h1>
      <p className="text-sm text-zinc-500 mb-4">Pipelines, cadences, assignment rules, health weights</p>

      <div className="flex gap-1 border-b border-zinc-200 mb-4">
        {["pipelines", "cadences", "assignment", "health"].map(t => (
          <button key={t} className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === t ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
            onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      {loading ? <p className="text-xs text-zinc-400">Loading...</p> : (
        <>
          {/* PIPELINES */}
          {tab === "pipelines" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Pipelines ({pipelines.length})</h2>
                <Button size="sm" className="h-7 text-[10px]" onClick={() => { setEditPipeline(null); setPipelineForm({ name: "", vertical: verticals[0] || "", stages: [] }); }} data-testid="new-pipeline-btn"><Plus size={12} className="mr-1" /> New Pipeline</Button>
              </div>
              {/* Existing pipelines list */}
              {pipelines.map(p => (
                <div key={p._id} className="border border-zinc-200 rounded-sm p-3" data-testid={`pipeline-${p._id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold">{p.name} <span className="text-zinc-400 font-normal">· {p.vertical}</span></p>
                      <p className="text-[10px] text-zinc-500">{(p.stages || []).length} stages: {(p.stages || []).map(s => s.name).join(" → ")}</p>
                    </div>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setEditPipeline(p._id); setPipelineForm({ name: p.name, vertical: p.vertical, stages: p.stages || [] }); }}>Edit</Button>
                  </div>
                </div>
              ))}
              {/* Pipeline Builder Form */}
              {(editPipeline !== null || pipelineForm.name !== "") && (
                <div className="border border-blue-200 rounded-sm p-4 bg-blue-50/30 space-y-3" data-testid="pipeline-builder">
                  <h3 className="text-xs font-semibold text-blue-800">{editPipeline ? "Edit Pipeline" : "New Pipeline"}</h3>
                  <div className="flex gap-2">
                    <Input placeholder="Pipeline Name" value={pipelineForm.name} onChange={e => setPipelineForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-xs" />
                    <select value={pipelineForm.vertical} onChange={e => setPipelineForm(f => ({ ...f, vertical: e.target.value }))} className="h-8 text-xs border border-zinc-200 rounded-sm px-2">{verticals.map(v => <option key={v} value={v}>{v}</option>)}</select>
                  </div>
                  <div className="space-y-2">
                    {pipelineForm.stages.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white border border-zinc-200 rounded-sm p-2">
                        <Input placeholder="Key" value={s.key} onChange={e => updateStage(i, "key", e.target.value)} className="h-7 text-xs w-24" />
                        <Input placeholder="Name" value={s.name} onChange={e => updateStage(i, "name", e.target.value)} className="h-7 text-xs w-32" />
                        <Input placeholder="SLA hrs" type="number" value={s.sla_hours} onChange={e => updateStage(i, "sla_hours", parseInt(e.target.value) || 0)} className="h-7 text-xs w-20" />
                        <label className="flex items-center gap-1 text-[10px]"><input type="checkbox" checked={s.is_terminal} onChange={e => updateStage(i, "is_terminal", e.target.checked)} /> Terminal</label>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => removeStage(i)}><Trash2 size={12} /></Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addStage}><Plus size={12} className="mr-1" /> Add Stage</Button>
                    <Button size="sm" className="h-7 text-[10px]" onClick={savePipeline}><Save size={12} className="mr-1" /> Save Pipeline</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CADENCES */}
          {tab === "cadences" && (
            <div>
              <h2 className="text-sm font-semibold mb-2">Cadences ({cadences.length})</h2>
              {cadences.length === 0 ? <p className="text-xs text-zinc-400 py-4">No cadences configured yet.</p> : cadences.map(c => (
                <div key={c._id} className="border border-zinc-200 rounded-sm p-3 mb-2">
                  <p className="text-xs font-semibold">{c.name} · {c.vertical}</p>
                  <p className="text-[10px] text-zinc-500">{(c.steps || []).length} steps</p>
                </div>
              ))}
            </div>
          )}

          {/* ASSIGNMENT */}
          {tab === "assignment" && (
            <div>
              <h2 className="text-sm font-semibold mb-2">Assignment Rules ({assignRules.length})</h2>
              {assignRules.length === 0 ? <p className="text-xs text-zinc-400 py-4">No assignment rules configured.</p> : assignRules.map(r => (
                <div key={r._id} className="border border-zinc-200 rounded-sm p-3 mb-2">
                  <p className="text-xs">{r.vertical} / {r.territory || r.country || "Global"} → Owner: {r.owner_user_id}</p>
                </div>
              ))}
            </div>
          )}

          {/* HEALTH */}
          {tab === "health" && (
            <div>
              <h2 className="text-sm font-semibold mb-2">Health Score Weights</h2>
              <pre className="text-xs bg-zinc-50 p-3 rounded-sm border border-zinc-200">{JSON.stringify(healthCfg.weights || {}, null, 2)}</pre>
            </div>
          )}
        </>
      )}
    </div>
  );
}
